import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface MobileBottomNavProps {
  activeTab: 'chat' | 'select' | 'conversations' | 'characters';
  setActiveTab: (tab: 'chat' | 'select' | 'conversations' | 'characters') => void;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const navItems = [
    {
      id: 'select' as const,
      label: 'Explore',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg>
      ),
    },
    {
      id: 'conversations' as const,
      label: 'Chat',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
        </svg>
      ),
    },
    {
      id: 'characters' as const,
      label: 'Create',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.5 7h-5c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5H15v6h2zm-11.5-6L10 8H8l-1.5 8zm-.5 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z" />
        </svg>
      ),
    },
    {
      id: 'profile' as const,
      label: 'Profile',
      isExternal: true,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      ),
    },
  ];

  const handleNavClick = (tabId: string) => {
    if (tabId === 'characters') {
      // Redirect to character creation page
      navigate('/create');
    } else if (tabId === 'conversations') {
      // Redirect to conversations page
      navigate('/conversations');
    } else if (tabId === 'profile') {
      // Navigate to user profile page
      if (user) {
        navigate(`/user/${user.id}`);
      }
    } else {
      setActiveTab(tabId as typeof activeTab);
    }
  };

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[60]">
      {/* Bottom Navigation Bar - Icon only style matching TopBar */}
      <div className="h-14 bg-dark-800/95 backdrop-blur-xl border-t border-white/10 shadow-2xl">
        <div className="h-full flex items-center justify-center px-4">
          <div className="flex items-center gap-8">
            {navItems.map((item) => {
              const isActive = item.isExternal ? false : activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`p-3 rounded-xl transition-all duration-300 relative ${
                    isActive
                      ? 'text-pink-400'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <div className="w-5 h-5">
                    {item.icon}
                  </div>
                  {isActive && (
                    <div className="absolute -top-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Safe area for devices with home indicator */}
      <div className="h-safe-area-inset-bottom bg-dark-900"></div>
    </div>
  );
};

export default MobileBottomNav;